import type {
  CartonAlias,
  CartonComponent,
  CartonItem,
} from '@skupreme/api-contracts/core';

export type CartonKind = 'items' | 'components';

/** An item or a component as one row of the carton table. */
export type CartonRow = {
  id: string;
  /** What the entity is saved under: the UPC for items, the id for components. */
  entityKey: string;
  title: string;
  image?: string;
  sku?: string;
  /** Where the SKU leads, when it leads anywhere. */
  skuLink?: string;
  identifier?: { label: string; value: string };
  /** The first supplier's terms; items do not get them from the API. */
  price?: number;
  leadTime?: number;
  moq?: number;
  entity: string;
  aliases: CartonAlias[];
  suppliers: { id: string; name: string; region?: string }[];
  /** For the alias popup: the item's UPC. */
  itemUpc?: string;
};

export function fromItem(item: CartonItem): CartonRow {
  return {
    id: item._id,
    entityKey: item.upc,
    title: item.p_title,
    image: item.p_images?.display_image ?? undefined,
    sku: item.p_sku,
    skuLink: item.supplier_id
      ? `/app/scm/supplier/${item.supplier_id}/?tab=items&item=${item._id}`
      : undefined,
    identifier: item.upc
      ? { label: item.unique_identifier_type || 'UPC', value: item.upc }
      : undefined,
    entity: item.entity ?? '',
    aliases: item.attributes ?? [],
    suppliers: (item.assignedSuppliers ?? []).map(supplier => ({
      id: supplier._id,
      name: supplier.supplier_name,
      region: supplier.region,
    })),
    itemUpc: item.item_upc,
  };
}

export function fromComponent(component: CartonComponent): CartonRow {
  return {
    id: component._id,
    entityKey: component._id,
    title: component.title ?? '',
    image: component.images?.[0],
    sku: component.sku,
    price: component.price,
    leadTime: component.lead_time,
    moq: component.moq,
    entity: component.entity ?? '',
    aliases: component.attributes ?? [],
    suppliers: (component.suppliers ?? []).map(supplier => ({
      id: supplier._id,
      name: supplier.name,
      region: supplier.country,
    })),
  };
}

/** The alias a single-carton row shows: the primary one, else the newest. */
export function mainAlias(row: CartonRow): CartonAlias | undefined {
  return row.aliases.find(alias => alias.primary) ?? row.aliases.at(-1);
}

/** Entities edited and not saved yet, by entity key. */
export function changedEntities(
  rows: CartonRow[],
  draft: Record<string, string>,
) {
  return rows
    .filter(
      row =>
        row.entityKey in draft &&
        draft[row.entityKey].trim() !== row.entity.trim(),
    )
    .map(row => ({ key: row.entityKey, entity: draft[row.entityKey].trim() }));
}
